
import { useState, useEffect } from 'react';
import { usePets } from '../hooks/usePets';
import { Plus, MapPin, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import PetListItem from '@/components/PetListItem';
import AddPetModal from '@/components/AddPetModal';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const MyPets = () => {
  const [isAddPetModalOpen, setIsAddPetModalOpen] = useState(false);
  const { pets, isLoading, error } = usePets();
  const { t } = useTranslation();
  const [placesBannerDismissed, setPlacesBannerDismissed] = useState<boolean>(
    typeof window !== 'undefined' && localStorage.getItem('pf_places_mypets_dismissed') === '1'
  );
  const placesVisited = typeof window !== 'undefined' && localStorage.getItem('pf_places_visited') === '1';
  const dismissPlacesBanner = () => {
    localStorage.setItem('pf_places_mypets_dismissed', '1');
    setPlacesBannerDismissed(true);
  };

  console.log('MyPets - Component state:', {
    isLoading,
    error: error?.message || null,
    petsCount: pets?.length || 0,
    pets: pets
  });

  if (error) {
    console.error('MyPets - Error state:', error);
    return (
      <div className="p-4 flex flex-col min-h-[60vh]">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-500 mb-2">{t('myPets.errorLoading')}</p>
            <Button variant="default" onClick={() => window.location.reload()}>
              {t('common.tryAgain')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (pets && pets.length > 0) {
    console.log('MyPets - Rendering pets:', pets);
    pets.forEach((pet) => {
      console.log('MyPets - Rendering individual pet:', pet);
    });
  }

  return (
    <div className="p-4 pb-24">
      <div className="flex justify-between items-center mb-6">
        <Button
          onClick={() => setIsAddPetModalOpen(true)}
          className="bg-pet-purple hover:bg-pet-purple/90 ml-auto"
        >
          <Plus className="h-5 w-5 mr-1" /> {t('myPets.addPet')}
        </Button>
      </div>

      <div className="max-w-2xl mx-auto">
        {isLoading ? (
          // Loading skeletons
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="mb-4 flex items-center">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="ml-4">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))
        ) : pets && pets.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pets.map((pet) => (
              <PetListItem key={pet.id} pet={pet} />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500 mb-4">{t('myPets.noPetsYet')}</p>
            <Button
              onClick={() => setIsAddPetModalOpen(true)}
              className="bg-pet-purple hover:bg-pet-purple/90"
            >
              <Plus className="h-5 w-5 mr-1" /> {t('myPets.addFirstPet')}
            </Button>
          </div>
        )}

        {pets && pets.length > 0 && !placesBannerDismissed && !placesVisited && (
          <div className="mt-6 relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-5 shadow-md">
            <button
              type="button"
              onClick={dismissPlacesBanner}
              aria-label={t('common.dismiss', 'Dismiss')}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <MapPin className="h-6 w-6 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-base">
                  {t('places.myPetsBannerTitle', {
                    defaultValue: 'Find pet-friendly places for {{name}}',
                    name: pets[0]?.name ?? t('places.myPetsBannerFallbackName', 'your pet'),
                  })}
                </div>
                <div className="text-sm text-white/90 mt-1">
                  {t('places.myPetsBannerSubtitle', 'Parks, cafés, restaurants and shops verified by the community.')}
                </div>
                <Link
                  to="/places"
                  className="inline-flex items-center gap-1 mt-3 text-sm font-semibold bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1.5"
                >
                  {t('places.myPetsBannerCta', 'Explore the map')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>


      <AddPetModal
        isOpen={isAddPetModalOpen}
        onClose={() => setIsAddPetModalOpen(false)}
      />
    </div>
  );
};

export default MyPets;
